/**
 * Barber Service History Page - Permanent, comprehensive history of all services
 * Includes filters, statistics, pagination, and export functionality
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getBarberByUserId } from '../services/barberService';
import { useServiceHistory } from '../hooks/useServiceHistory';
import {
  Calendar,
  DollarSign,
  Download,
  Clock,
  CheckCircle,
  Filter,
  TrendingUp,
  ChevronDown,
  User,
  Award,
  XCircle,
  AlertCircle,
  FileText,
} from 'lucide-react';
import type { ServiceHistoryEntry } from '../types/serviceHistory';

export default function BarberHistoryPage() {
  const { firebaseUser, loading: authLoading } = useAuth();
  const [barberId, setBarberId] = useState<string | undefined>(undefined);
  const [dateRange, setDateRange] = useState({
    start: getDefaultStartDate(),
    end: new Date(),
  });

  // Fetch barber ID
  useEffect(() => {
    async function fetchBarberId() {
      // Wait for auth to finish loading
      if (authLoading) return;

      if (!firebaseUser?.uid) {
        setBarberId(undefined);
        return;
      }

      try {
        const barber = await getBarberByUserId(firebaseUser.uid);
        if (barber) {
          setBarberId(barber.barberId);
        } else {
          setBarberId(undefined);
        }
      } catch (error) {
        console.error('Error fetching barber:', error);
        setBarberId(undefined);
      }
    }

    fetchBarberId();
  }, [firebaseUser?.uid, authLoading]);

  // Use service history hook
  const {
    groupedEntries,
    stats,
    loading,
    loadingMore,
    error,
    setFilters,
    pagination,
    loadMore,
    exportToCSV,
    exportToPDF,
  } = useServiceHistory({
    barberId,
    initialPageSize: 30,
    autoLoad: !!barberId,
  });

  // Update filters when date range changes
  useEffect(() => {
    if (dateRange.start && dateRange.end) {
      setFilters({ dateRange });
    }
  }, [dateRange.start, dateRange.end]);

  const handleQuickFilter = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    setDateRange({ start, end });
  };

  const getClientTypeBadge = (clientType?: string) => {
    if (!clientType) return null;

    const badges = {
      first_time: { color: 'bg-info/10 text-info bg-info/10 text-info', icon: User, label: 'Nuevo' },
      regular: { color: 'bg-surface-2 text-ink-muted bg-surface-2 text-ink-muted', icon: User, label: 'Regular' },
      vip: { color: 'bg-accent/10 text-accent bg-accent/10 text-accent', icon: Award, label: 'VIP' },
    };

    const badge = badges[clientType as keyof typeof badges];
    if (!badge) return null;

    const Icon = badge.icon;

    return (
      <span className={`flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded ${badge.color}`}>
        <Icon size={12} />
        {badge.label}
      </span>
    );
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      completed: { color: 'bg-success/10 text-success bg-success/10 text-success', label: 'Completado', icon: CheckCircle },
      cancelled: { color: 'bg-accent/10 text-accent bg-accent/10 text-accent', label: 'Cancelado', icon: XCircle },
      no_show: { color: 'bg-danger/10 text-danger bg-danger/10 text-danger', label: 'No Show', icon: AlertCircle },
    };

    const badge = badges[status as keyof typeof badges];
    if (!badge) return null;

    const Icon = badge.icon;

    return (
      <span className={`flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded ${badge.color}`}>
        <Icon size={12} />
        {badge.label}
      </span>
    );
  };

  if (loading && !barberId) {
    return (
      <div className="min-h-screen bg-surface-2 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-info/30 mx-auto mb-4"></div>
          <p className="text-ink-muted">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!barberId) {
    return (
      <div className="min-h-screen bg-surface-2 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-danger mx-auto mb-4" />
          <p className="text-ink-muted">
            No se encontró el perfil de barbero
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-2 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-8">
          <div>
            <h1 className="text-3xl font-bold text-ink mb-2">
              Mi Historial
            </h1>
            <p className="text-ink-muted">
              Historial completo de servicios realizados
            </p>
          </div>
          <div className="flex gap-2 mt-4 md:mt-0">
            <button
              onClick={exportToCSV}
              disabled={groupedEntries.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-success text-ink rounded-lg hover:bg-success/90 disabled:bg-surface-3 disabled:cursor-not-allowed transition shadow-lg"
            >
              <Download size={18} />
              CSV
            </button>
            <button
              onClick={exportToPDF}
              disabled={groupedEntries.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-danger text-ink rounded-lg hover:bg-danger/90 disabled:bg-surface-3 disabled:cursor-not-allowed transition shadow-lg"
            >
              <FileText size={18} />
              PDF
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-surface rounded-xl p-6 shadow-lg mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-info" />
            <h2 className="text-lg font-bold text-ink">Filtros</h2>
          </div>

          <div className="space-y-4">
            {/* Quick Filters */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleQuickFilter(0)}
                className="px-4 py-2 bg-info/10 text-info rounded-lg hover:bg-info/20 transition text-sm"
              >
                Hoy
              </button>
              <button
                onClick={() => handleQuickFilter(7)}
                className="px-4 py-2 bg-info/10 text-info rounded-lg hover:bg-info/20 transition text-sm"
              >
                Última semana
              </button>
              <button
                onClick={() => handleQuickFilter(30)}
                className="px-4 py-2 bg-info/10 text-info rounded-lg hover:bg-info/20 transition text-sm"
              >
                Último mes
              </button>
              <button
                onClick={() => handleQuickFilter(90)}
                className="px-4 py-2 bg-info/10 text-info rounded-lg hover:bg-info/20 transition text-sm"
              >
                Últimos 3 meses
              </button>
            </div>

            {/* Date Range Inputs */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ink-muted mb-2">
                  Fecha Inicio
                </label>
                <input
                  type="date"
                  value={dateRange.start.toISOString().split('T')[0]}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, start: new Date(e.target.value) })
                  }
                  className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface text-ink"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-muted mb-2">
                  Fecha Fin
                </label>
                <input
                  type="date"
                  value={dateRange.end.toISOString().split('T')[0]}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, end: new Date(e.target.value) })
                  }
                  className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface text-ink"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Total Services */}
          <div className="rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="w-8 h-8 opacity-80" />
              <span className="text-3xl font-black">{stats.totalServices}</span>
            </div>
            <h3 className="text-sm font-medium opacity-90">Total Servicios</h3>
            <p className="text-xs opacity-75 mt-1">
              {stats.completedServices} completados
            </p>
          </div>

          {/* Total Earnings */}
          <div className="rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-8 h-8 opacity-80" />
              <span className="text-3xl font-black">
                €{(stats.totalEarnings / 100).toFixed(2)}
              </span>
            </div>
            <h3 className="text-sm font-medium opacity-90">Ingresos Totales</h3>
            <p className="text-xs opacity-75 mt-1">
              Promedio: €{(stats.averageEarnings / 100).toFixed(2)}
            </p>
          </div>

          {/* Average Duration */}
          <div className="rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <Clock className="w-8 h-8 opacity-80" />
              <span className="text-3xl font-black">{stats.averageDuration}m</span>
            </div>
            <h3 className="text-sm font-medium opacity-90">Duración Promedio</h3>
            <p className="text-xs opacity-75 mt-1">
              Total: {Math.round(stats.totalDuration / 60)}h
            </p>
          </div>

          {/* Popular Service */}
          <div className="rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-8 h-8 opacity-80" />
              <span className="text-xl font-black truncate">
                {stats.mostPopularService?.serviceName || 'Sin datos'}
              </span>
            </div>
            <h3 className="text-sm font-medium opacity-90">Servicio Más Popular</h3>
            <p className="text-xs opacity-75 mt-1">
              {stats.mostPopularService?.count
                ? `${stats.mostPopularService.count} veces`
                : 'No hay servicios en el período'}
            </p>
          </div>
        </div>

        {/* Service History List */}
        {loading ? (
          <div className="bg-surface rounded-xl shadow-lg p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-info/30 mx-auto mb-4"></div>
            <p className="text-ink-muted">Cargando historial...</p>
          </div>
        ) : error ? (
          <div className="bg-surface rounded-xl shadow-lg p-12 text-center">
            <AlertCircle className="w-16 h-16 text-danger mx-auto mb-4" />
            <p className="text-ink-muted">Error cargando historial</p>
            <p className="text-sm text-ink-subtle mt-2">{error.message}</p>
          </div>
        ) : groupedEntries.length === 0 ? (
          <div className="bg-surface rounded-xl shadow-lg p-12 text-center">
            <Calendar className="w-16 h-16 text-ink-muted mx-auto mb-4" />
            <p className="text-ink-muted">
              No hay servicios en el periodo seleccionado
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {groupedEntries.map((group) => (
              <div
                key={group.date}
                className="bg-surface rounded-xl shadow-lg overflow-hidden"
              >
                {/* Date Header */}
                <div className="px-6 py-4">
                  <h3 className="text-lg font-bold text-white">
                    {formatDate(group.date)}
                  </h3>
                  <div className="flex gap-4 mt-2 text-sm text-info">
                    <span>{group.dailyStats.totalServices} servicios</span>
                    <span>•</span>
                    <span>€{(group.dailyStats.totalEarnings / 100).toFixed(2)}</span>
                    <span>•</span>
                    <span>{group.dailyStats.totalDuration}m</span>
                  </div>
                </div>

                {/* Service Cards */}
                <div className="divide-y divide-hairline">
                  {group.entries.map((entry) => (
                    <ServiceCard key={entry.queueId} entry={entry} />
                  ))}
                </div>
              </div>
            ))}

            {/* Load More Button */}
            {pagination.hasMore && (
              <div className="flex justify-center pt-6">
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="flex items-center gap-2 px-6 py-3 bg-info text-ink rounded-lg hover:bg-info/90 disabled:bg-surface-3 disabled:cursor-not-allowed transition shadow-lg"
                >
                  {loadingMore ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                      <span>Cargando...</span>
                    </>
                  ) : (
                    <>
                      <ChevronDown size={20} />
                      <span>Cargar más</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  function ServiceCard({ entry }: { entry: ServiceHistoryEntry }) {
    const statusStyle = entry.status === 'completed'
      ? ''
      : entry.status === 'cancelled'
      ? 'bg-accent/10'
      : 'bg-danger/10';

    return (
      <div className={`p-6 hover:bg-surface-2 transition ${statusStyle}`}>
        <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
          {/* Left: Client & Service Info */}
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <h4 className="text-lg font-semibold text-ink">
                {entry.clientName}
              </h4>
              {getClientTypeBadge(entry.clientType)}
              {getStatusBadge(entry.status)}
            </div>

            <p className="text-sm text-ink-muted mb-1">
              {entry.serviceName || 'Servicio no especificado'}
            </p>

            {entry.clientEmail && (
              <p className="text-xs text-ink-subtle text-ink-muted">
                {entry.clientEmail}
              </p>
            )}
          </div>

          {/* Right: Ticket Number & Time */}
          <div className="text-left md:text-right">
            <span className="font-mono text-sm text-ink-subtle">
              #{entry.ticketNumber}
            </span>
            <p className="text-xs text-ink-subtle mt-1">
              {entry.completedAt.toDate().toLocaleTimeString('es-ES', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>
          </div>
        </div>

        {/* Bottom: Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          {/* Duration */}
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-ink-subtle" />
            <span className="text-sm text-ink-muted">
              {entry.serviceDuration}m
            </span>
          </div>

          {/* Amount */}
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-success" />
            <span className="text-sm font-semibold text-ink">
              €{(entry.amountCharged / 100).toFixed(2)}
            </span>
          </div>

          {/* Points Earned */}
          {entry.loyaltyPointsEarned > 0 && (
            <div className="flex items-center gap-2">
              <Award className="w-4 h-4 text-accent" />
              <span className="text-sm text-accent font-semibold">
                +{entry.loyaltyPointsEarned} pts
              </span>
            </div>
          )}

          {/* Discount Applied */}
          {entry.discountApplied > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-warning font-semibold">
                -{(entry.discountApplied / 100).toFixed(2)}€
              </span>
            </div>
          )}
        </div>

        {/* Cancel Reason */}
        {entry.status === 'cancelled' && entry.cancelReason && (
          <div className="mt-3 flex items-start gap-2 p-2 bg-accent/10 rounded">
            <AlertCircle className="w-4 h-4 text-accent mt-0.5" />
            <p className="text-sm text-accent">
              {entry.cancelReason}
            </p>
          </div>
        )}
      </div>
    );
  }
}

// Helper functions
function getDefaultStartDate(): Date {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return date;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (dateString === today.toISOString().split('T')[0]) {
    return 'Hoy';
  } else if (dateString === yesterday.toISOString().split('T')[0]) {
    return 'Ayer';
  } else {
    return date.toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }
}
